/**
 * useToFinchSuggestionCard - Hook for managing TO_FINCH suggestion card state
 *
 * Consolidates:
 * - UI state (expand, modal)
 * - Action state (accepting, rejecting)
 * - Payload editing (simple 4-field schema)
 * - Derived data (confidence, reasoning)
 */

import { useCallback, useMemo, useReducer } from 'react';

import type {
  FilevineEntitySyncSuggestionSchema,
  FilevineEntitySyncSuggestionSetSchema,
} from '@/services/api/generated/entitySyncService';

import { normalizeConfidence } from '../../utils/confidenceUtils';
import type {
  ActionStatus,
  BaseCardAction,
  ToFinchSuggestionCardState,
} from './types';

// ============================================================================
// Types
// ============================================================================

interface UseToFinchSuggestionCardProps {
  suggestion: FilevineEntitySyncSuggestionSchema;
  parentSet: FilevineEntitySyncSuggestionSetSchema;
  onAccept: (
    suggestion: FilevineEntitySyncSuggestionSchema,
    payload?: Record<string, unknown>,
  ) => Promise<void>;
  onReject: (id: string) => Promise<void>;
}

interface State {
  isExpanded: boolean;
  isEditModalOpen: boolean;
  actionStatus: ActionStatus;
  editedPayload: Record<string, unknown> | null;
  isPayloadModified: boolean;
}

type Action =
  | BaseCardAction
  | {
      type: 'UPDATE_FIELD';
      fieldName: string;
      value: string | null;
    }
  | { type: 'RESET_PAYLOAD'; payload: Record<string, unknown> | null };

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get initial payload from suggestion
 */
function getInitialPayload(
  suggestion: FilevineEntitySyncSuggestionSchema,
): Record<string, unknown> | null {
  const payload = (suggestion.modified_payload ??
    suggestion.suggested_payload) as Record<string, unknown> | undefined;
  return payload ?? null;
}

// ============================================================================
// Reducer
// ============================================================================

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'TOGGLE_EXPAND':
      return { ...state, isExpanded: !state.isExpanded };

    case 'SET_EDIT_MODAL':
      return { ...state, isEditModalOpen: action.open };

    case 'START_ACCEPT':
      return { ...state, actionStatus: 'accepting' };

    case 'START_REJECT':
      return { ...state, actionStatus: 'rejecting' };

    case 'ACTION_COMPLETE':
      return { ...state, actionStatus: 'idle' };

    case 'UPDATE_FIELD': {
      if (!state.editedPayload) return state;
      return {
        ...state,
        editedPayload: {
          ...state.editedPayload,
          [action.fieldName]: action.value,
        },
        isPayloadModified: true,
      };
    }

    case 'RESET_PAYLOAD':
      return {
        ...state,
        editedPayload: action.payload,
        isPayloadModified: false,
      };

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useToFinchSuggestionCard({
  suggestion,
  parentSet,
  onAccept,
  onReject,
}: UseToFinchSuggestionCardProps): ToFinchSuggestionCardState {
  const defaultExpanded = suggestion.status !== 'COMPLETED';

  const initialPayload = useMemo(
    () => getInitialPayload(suggestion),
    [suggestion],
  );

  const [state, dispatch] = useReducer(reducer, {
    isExpanded: defaultExpanded,
    isEditModalOpen: false,
    actionStatus: 'idle',
    editedPayload: initialPayload,
    isPayloadModified: false,
  });

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------

  const confidence = useMemo(
    () => normalizeConfidence(parentSet.confidence),
    [parentSet.confidence],
  );

  const aiReasoning = suggestion.reasoning ?? undefined;

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const toggleExpand = useCallback(() => {
    dispatch({ type: 'TOGGLE_EXPAND' });
  }, []);

  const setEditModalOpen = useCallback((open: boolean) => {
    dispatch({ type: 'SET_EDIT_MODAL', open });
  }, []);

  const updateField = useCallback(
    (fieldName: string, value: string | null) => {
      dispatch({ type: 'UPDATE_FIELD', fieldName, value });
    },
    [],
  );

  const resetPayload = useCallback(() => {
    dispatch({ type: 'RESET_PAYLOAD', payload: initialPayload });
  }, [initialPayload]);

  const handleAccept = useCallback(async () => {
    dispatch({ type: 'START_ACCEPT' });
    try {
      // Pass modified payload if edits were made
      const payloadToSend =
        state.isPayloadModified && state.editedPayload
          ? state.editedPayload
          : undefined;
      await onAccept(suggestion, payloadToSend);
    } finally {
      dispatch({ type: 'ACTION_COMPLETE' });
    }
  }, [onAccept, suggestion, state.isPayloadModified, state.editedPayload]);

  const handleApprove = useCallback(async () => {
    dispatch({ type: 'START_ACCEPT' });
    try {
      await onAccept(suggestion, undefined);
    } finally {
      dispatch({ type: 'ACTION_COMPLETE' });
    }
  }, [onAccept, suggestion]);

  const handleModify = useCallback(async () => {
    dispatch({ type: 'START_ACCEPT' });
    try {
      const payloadToSend =
        state.isPayloadModified && state.editedPayload
          ? state.editedPayload
          : undefined;
      await onAccept(suggestion, payloadToSend);
    } finally {
      dispatch({ type: 'ACTION_COMPLETE' });
    }
  }, [onAccept, suggestion, state.isPayloadModified, state.editedPayload]);

  const handleReject = useCallback(async () => {
    dispatch({ type: 'START_REJECT' });
    try {
      await onReject(suggestion.id);
    } finally {
      dispatch({ type: 'ACTION_COMPLETE' });
    }
  }, [onReject, suggestion.id]);

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  return {
    // UI state
    isExpanded: state.isExpanded,
    isEditModalOpen: state.isEditModalOpen,

    // Action state
    isAccepting: state.actionStatus === 'accepting',
    isRejecting: state.actionStatus === 'rejecting',
    isActionInProgress: state.actionStatus !== 'idle',

    // Payload editing
    editedPayload: state.editedPayload,
    isPayloadModified: state.isPayloadModified,
    updateField,
    resetPayload,

    // Derived
    confidence,
    aiReasoning,

    // Actions
    toggleExpand,
    setEditModalOpen,
    handleAccept,
    handleApprove,
    handleModify,
    handleReject,
  };
}

export default useToFinchSuggestionCard;
